import React, { useEffect, useRef } from 'react';
import { defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';
import { MediaPlayer, MediaProvider, Poster, useMediaState } from '@vidstack/react';

import {SimpleImage} from './image-simple.jsx';
import {IconPlay} from '$partials/icons';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/media.d.ts').Video}
 */
const Video = ({
    height,
    onFullscreenChange,
    onPlayingStateChange,
    playButtonPosition = 'bottom-right',
    poster,
    src,
    title = '',
    width,
    autoPlay,
    loop,
    showControls = true,
}) => {
    const playerRef = useRef(null);

    const startPlay = () => {
        playerRef.current.play?.();
    };
    const isStarted = useMediaState('started', playerRef);
    const isPlaying = useMediaState('playing', playerRef);

    useEffect(() => {
        if (onPlayingStateChange) {
            onPlayingStateChange(!isPlaying);
        }
    }, [isPlaying, onPlayingStateChange]);

    return (
        <MediaPlayer
            crossOrigin
            playsInline
            aspectRatio={width && height ? `${width}/${height}` : '16/9'}
            autoPlay={autoPlay}
            className='rounded-lg overflow-hidden'
            controls={showControls}
            load={autoPlay ? 'visible' : 'play'}
            loop={loop}
            muted={autoPlay}
            onFocus={() => {
                playerRef.current.controls?.show?.();
            }}
            onFullscreenChange={onFullscreenChange}
            posterLoad='eager'
            ref={playerRef}
            src={src}
            title={title}>
            <MediaProvider>
                {poster && (
                    <div
                        className={`${isStarted ? 'hidden' : ''}`}>
                        <SimpleImage src={poster} />
                    </div>
                )}
                {!isStarted && showControls && (
                    <div className={`absolute z-50
                        ${playButtonPosition === 'bottom-right' ? 'right-8 bottom-8' : ''}
                        ${playButtonPosition === 'center' ? 'left-0 right-0 text-center' : ''}
                    `}>
                        <button
                            className='p-12 inline-flex items-center type-style-7 bg-button-tint-surface rounded-full'
                            onClick={startPlay.bind(null)}
                            type='button'>
                            <IconPlay/>
                        </button>
                    </div>
                )}
            </MediaProvider>
            {isStarted && showControls && (
                <DefaultVideoLayout
                    icons={defaultLayoutIcons}
                    slots={{
                        title: null,
                        chapterTitle: null,
                        airPlayButton: null,
                        googleCastButton: null
                    }} />
            )}
        </MediaPlayer>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default Video;
